import React from "react";
import {
  Card,
  InlineStack,
  Text,
  Badge,
  Button,
  BlockStack,
  Box,
  Collapsible,
  Divider,
} from "@shopify/polaris";
import { ChevronDownIcon, ChevronUpIcon } from "@shopify/polaris-icons";
import VideoRow from "./VideoRow";

/**
 * Course Card Component
 * Expandable card showing course info and videos
 */
const CourseCard = ({
  course,
  isExpanded,
  selectedVideos,
  onToggle,
  onToggleVideo,
  onSelectAll,
  onDeselectAll,
}) => {
  const getFirstNonNullValue = (obj) => {
    if (!obj) return "Untitled";
    for (const value of Object.values(obj)) {
      if (value !== null) return value;
    }
    return "Untitled";
  };

  const courseTitle = getFirstNonNullValue(course.title);
  const hasVideos = course.videos && course.videos.length > 0;
  const mp4Videos = course.videos?.filter((v) => v.status === "mp4") || [];
  const allMp4Selected =
    mp4Videos.length > 0 &&
    mp4Videos.every((v) => selectedVideos.has(v.id));

  return (
    <div style={{ marginBottom: "16px" }}>
      <Card>
        <Box padding="4">
          <InlineStack align="space-between" blockAlign="center" wrap={false}>
            {/* Course Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <BlockStack gap="2">
                <Text as="h2" variant="headingLg" truncate>
                  {courseTitle}
                </Text>

                <InlineStack gap="2">
                  <Badge tone="info">
                    Total: {course.total_videos || 0}
                  </Badge>
                  <Badge tone="success">
                    ✅ {course.hls_count || 0} HLS
                  </Badge>
                  <Badge tone="warning">
                    ⚠️ {course.mp4_count || 0} MP4
                  </Badge>
                  {course.converting_count > 0 && (
                    <Badge tone="attention">
                      ⟳ {course.converting_count} Converting
                    </Badge>
                  )}
                </InlineStack>
              </BlockStack>
            </div>

            {/* Actions */}
            <InlineStack gap="2">
              {isExpanded && hasVideos && mp4Videos.length > 0 && (
                <Button
                  onClick={allMp4Selected ? onDeselectAll : onSelectAll}
                  size="slim"
                >
                  {allMp4Selected ? "Deselect All" : "Select All MP4"}
                </Button>
              )}

              <Button
                onClick={onToggle}
                icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
              >
                {isExpanded ? "Collapse" : "Expand"}
              </Button>
            </InlineStack>
          </InlineStack>
        </Box>

        {/* Videos List */}
        <Collapsible
          open={isExpanded}
          id={`course-${course.id}`}
          transition={{ duration: "200ms", timingFunction: "ease-in-out" }}
        >
          <Divider />
          <Box padding="4" background="bg-surface-secondary">
            {!course.videosLoaded ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Text as="p" tone="subdued">
                  Loading videos...
                </Text>
              </div>
            ) : !hasVideos ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Text as="p" tone="subdued">
                  No videos in this course
                </Text>
              </div>
            ) : (
              <BlockStack gap="2">
                {course.videos.map((video) => (
                  <VideoRow
                    key={video.id}
                    video={video}
                    isSelected={selectedVideos.has(video.id)}
                    onToggle={() => onToggleVideo(video.id)}
                  />
                ))}
              </BlockStack>
            )}
          </Box>
        </Collapsible>
      </Card>
    </div>
  );
};

export default CourseCard;